import { createContext, useContext } from 'react';
import type { ComponentProps, MouseEvent } from 'react';
import { cn } from '../../lib/utils';

/** The look of an action in a data-table row (`ActionLink`s inside `RowActions` get it automatically) */
export const rowActionClasses =
  'border-primary/30 text-primary hover:border-primary/50 hover:bg-accent hover:text-accent-foreground';

const RowActionsContext = createContext(false);

/** `true` inside `RowActions` */
export const useIsInRowActions = () => useContext(RowActionsContext);

export interface RowActionsProps extends ComponentProps<'div'> {
  orientation?: 'row' | 'column';
  stopPropagation?: boolean;
}

/**
 * Layout wrapper for actions inside a clickable data-table row.
 *
 * Actions should not open the row's configured destination when clicked.
 */
export function RowActions({
  children,
  className,
  onClick,
  orientation = 'row',
  stopPropagation = true,
  ...props
}: RowActionsProps) {
  const handleClick = stopPropagation
    ? (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        onClick?.(event);
      }
    : onClick;

  return (
    <RowActionsContext.Provider value={true}>
      {/* This div is an event boundary for actions inside a clickable table row. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        {...props}
        className={cn(
          orientation === 'column'
            ? 'flex flex-col items-stretch gap-2'
            : 'flex gap-2',
          className
        )}
        onClick={handleClick}
      >
        {children}
      </div>
    </RowActionsContext.Provider>
  );
}
